import { useMemo } from "react";
import { useOrgSettings } from "@/hooks/use-org-settings";
import {
  useCollectionPromotions,
  useCustomPromotions,
  useMenuProducts,
  useMenuSections,
  useSectionPromotions,
} from "@/hooks/use-menu";
import {
  useActiveOrganization,
  useOrganizationInvitations,
  useOrganizationMembers,
} from "@/hooks/use-organizations";
import { useSubscriptions } from "@/hooks/use-subscriptions";
import type { Subscription } from "@/hooks/use-subscriptions";
import type { MenuProduct, MenuPromotion } from "@/types/menu";

const CHART_WINDOW_DAYS = 90;
const EXPIRING_SOON_DAYS = 7;
const MS_PER_DAY = 86_400_000;
const RECENT_PRODUCT_LIMIT = 10;
const DEFAULT_CURRENCY = "UYU";

export type ChecklistState = {
  hasLogo: boolean;
  hasSection: boolean;
  hasProduct: boolean;
  hasTeam: boolean;
  hasSubscription: boolean;
  allComplete: boolean;
};

export type ActivePromotion = {
  id: string;
  name: string;
  discount: number;
  startDate: string | null;
  endDate: string | null;
  progress: number;
  endsInDays: number | null;
  kind: "custom" | "section" | "collection";
};

export type HomeData = {
  isLoading: boolean;
  organizationId: string | null;
  organizationName: string | null;
  organizationSlug: string | null;
  organizationLogo: string | null;
  businessType: "restaurant" | "supermarket";
  currency: string;
  kpis: {
    products: number;
    visibleSections: number;
    activePromotions: number;
    expiringSoon: number;
    members: number;
    pendingInvites: number;
  };
  subscription: Subscription | null;
  checklist: ChecklistState;
  chart: { date: string; count: number }[];
  recentProducts: MenuProduct[];
  activePromotions: ActivePromotion[];
};

function startOfUtcDay(timestamp: number) {
  return Math.floor(timestamp / MS_PER_DAY) * MS_PER_DAY;
}

function buildChartSeries(products: MenuProduct[]) {
  const today = startOfUtcDay(Date.now());
  const buckets = new Map<number, number>();

  for (const product of products) {
    const created = Date.parse(product.createdAt);
    if (Number.isNaN(created)) {
      continue;
    }
    const day = startOfUtcDay(created);
    if (day < today - (CHART_WINDOW_DAYS - 1) * MS_PER_DAY) {
      continue;
    }
    buckets.set(day, (buckets.get(day) ?? 0) + 1);
  }

  const series: { date: string; count: number }[] = [];
  for (let i = CHART_WINDOW_DAYS - 1; i >= 0; i--) {
    const day = today - i * MS_PER_DAY;
    series.push({
      date: new Date(day).toISOString().slice(0, 10),
      count: buckets.get(day) ?? 0,
    });
  }
  return series;
}

function isPromotionActiveNow(promotion: MenuPromotion, now: number) {
  if (!promotion.active) {
    return false;
  }
  const start = promotion.startDate ? Date.parse(promotion.startDate) : null;
  const end = promotion.endDate ? Date.parse(promotion.endDate) : null;
  if (start !== null && !Number.isNaN(start) && now < start) {
    return false;
  }
  if (end !== null && !Number.isNaN(end) && now > end) {
    return false;
  }
  return true;
}

function toActivePromotion(
  promotion: MenuPromotion,
  kind: ActivePromotion["kind"],
  now: number,
): ActivePromotion {
  const start = promotion.startDate ? Date.parse(promotion.startDate) : null;
  const end = promotion.endDate ? Date.parse(promotion.endDate) : null;

  let progress = 0;
  let endsInDays: number | null = null;

  if (end !== null && !Number.isNaN(end)) {
    endsInDays = Math.max(0, Math.ceil((end - now) / MS_PER_DAY));
    if (start !== null && !Number.isNaN(start) && end > start) {
      progress = Math.min(100, Math.max(0, ((now - start) / (end - start)) * 100));
    } else {
      progress = 50;
    }
  }

  return {
    id: promotion.id,
    name: promotion.name,
    discount: promotion.discount,
    startDate: promotion.startDate,
    endDate: promotion.endDate,
    progress,
    endsInDays,
    kind,
  };
}

export function useHomeData(): HomeData {
  const { data: activeOrg, isLoading: orgLoading } = useActiveOrganization();
  const orgId = activeOrg?.id;

  const { data: settings } = useOrgSettings(orgId);
  const { data: productsRes, isLoading: productsLoading } = useMenuProducts(orgId);
  const { data: sectionsRes, isLoading: sectionsLoading } = useMenuSections(orgId);
  const { data: customPromosRes, isLoading: customLoading } = useCustomPromotions(orgId);
  const { data: sectionPromosRes, isLoading: sectionPromosLoading } = useSectionPromotions(orgId);
  const { data: collectionPromosRes, isLoading: collectionPromosLoading } =
    useCollectionPromotions(orgId);
  const { data: members, isLoading: membersLoading } = useOrganizationMembers(orgId);
  const { data: invitations } = useOrganizationInvitations(orgId);
  const { data: subscriptionsRes, isLoading: subsLoading } = useSubscriptions();

  const isLoading =
    orgLoading ||
    productsLoading ||
    sectionsLoading ||
    customLoading ||
    sectionPromosLoading ||
    collectionPromosLoading ||
    membersLoading ||
    subsLoading;

  return useMemo<HomeData>(() => {
    const products = productsRes?.products ?? [];
    const sections = sectionsRes?.sections ?? [];
    const now = Date.now();

    const activePromotions: ActivePromotion[] = [
      ...(customPromosRes?.promotions ?? [])
        .filter((p) => isPromotionActiveNow(p, now))
        .map((p) => toActivePromotion(p, "custom", now)),
      ...(sectionPromosRes?.promotions ?? [])
        .filter((p) => isPromotionActiveNow(p, now))
        .map((p) => toActivePromotion(p, "section", now)),
      ...(collectionPromosRes?.promotions ?? [])
        .filter((p) => isPromotionActiveNow(p, now))
        .map((p) => toActivePromotion(p, "collection", now)),
    ].sort((a, b) => {
      if (a.endsInDays === null && b.endsInDays === null) {
        return 0;
      }
      if (a.endsInDays === null) {
        return 1;
      }
      if (b.endsInDays === null) {
        return -1;
      }
      return a.endsInDays - b.endsInDays;
    });

    const expiringSoon = activePromotions.filter(
      (p) => p.endsInDays !== null && p.endsInDays <= EXPIRING_SOON_DAYS,
    ).length;

    const visibleSections = sections.filter((s) => s.visible).length;

    const pendingInvites = (invitations ?? []).filter((inv) => inv.status === "pending").length;

    const subscription =
      (subscriptionsRes?.subscriptions ?? []).find((s) => s.status === "active") ??
      subscriptionsRes?.subscriptions?.[0] ??
      null;

    const hasLogo = !!activeOrg?.logo;
    const hasSection = sections.length > 0;
    const hasProduct = products.length > 0;
    const hasTeam = (members?.length ?? 0) > 1 || pendingInvites > 0;
    const hasSubscription = subscription?.status === "active";

    const checklist: ChecklistState = {
      hasLogo,
      hasSection,
      hasProduct,
      hasTeam,
      hasSubscription,
      allComplete: hasLogo && hasSection && hasProduct && hasTeam && hasSubscription,
    };

    const recentProducts = [...products]
      .sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt))
      .slice(0, RECENT_PRODUCT_LIMIT);

    return {
      isLoading,
      organizationId: activeOrg?.id ?? null,
      organizationName: activeOrg?.name ?? null,
      organizationSlug: activeOrg?.slug ?? null,
      organizationLogo: activeOrg?.logo ?? null,
      businessType: activeOrg?.businessType ?? "restaurant",
      currency: settings?.currency ?? DEFAULT_CURRENCY,
      kpis: {
        products: products.length,
        visibleSections,
        activePromotions: activePromotions.length,
        expiringSoon,
        members: members?.length ?? 0,
        pendingInvites,
      },
      subscription,
      checklist,
      chart: buildChartSeries(products),
      recentProducts,
      activePromotions,
    };
  }, [
    isLoading,
    activeOrg,
    settings,
    productsRes,
    sectionsRes,
    customPromosRes,
    sectionPromosRes,
    collectionPromosRes,
    members,
    invitations,
    subscriptionsRes,
  ]);
}
